/**
 * The rich-text HTML contract, shared verbatim with
 * `zh-backend/src/common/helpers/rich-text.helper.ts` and
 * `zh-mobile-redesign/src/utils/richText.ts`.
 *
 * There is no shared package across the three repos, so these helpers are triplicated on
 * purpose and each copy carries an identical unit-test table. If the three implementations
 * ever drift, one of the three suites fails — that is the drift alarm.
 *
 * Code blocks are deliberately absent: the mobile editor (TenTap) has no CodeBlock
 * extension, and ProseMirror silently discards nodes it does not know, so a `<pre>` written
 * on web would be destroyed the first time the task was saved from a phone.
 */
export const RICH_TEXT_ALLOWED_TAGS = [
  'p',
  'br',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'ul',
  'ol',
  'li',
  'h1',
  'h2',
  'h3',
  'blockquote',
  'a',
];

/**
 * Matches an opening or closing tag from our own allowlist — deliberately NOT "any `<...>`".
 *
 * A legacy plain-text description such as `use rebar < 5mm > spec` must not be mistaken for
 * markup, because every caller branches on this predicate to decide whether a value is
 * legacy plain text or rich HTML.
 */
const HTML_TAG_RE = /<\/?(?:p|br|ul|ol|li|h[1-3]|blockquote|strong|em|u|b|i|a)\b[^>]*>/i;

export const isProbablyHtml = (value?: string | null): boolean =>
  !!value && HTML_TAG_RE.test(value);

/**
 * Flatten rich HTML to readable plain text, preserving block boundaries as newlines.
 *
 * Used for clamped previews and anywhere a plain-text field is seeded from a task
 * description — notably `BudgetRequestModal`, whose value flows into a subsystem that will
 * never render HTML. Legacy plain text is returned untouched.
 */
export const htmlToPlainText = (html?: string | null): string => {
  if (!html) {
    return '';
  }
  if (!isProbablyHtml(html)) {
    return html;
  }

  return (
    html
      // Paragraph-level blocks become a blank line so the document's structure survives the
      // trip into a plain <textarea>; list items are only one line apart, or every bulleted
      // list would come out double-spaced.
      .replace(/<\/(p|div|h[1-6]|blockquote|pre)\s*>/gi, '\n\n')
      .replace(/<\/li\s*>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<[^>]+>/g, '')
      // `&amp;` is decoded last: decoding it first would turn the literal text `&amp;lt;`
      // into `<` instead of `&lt;`.
      .replace(/&nbsp;/gi, ' ')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#0?39;|&apos;/gi, "'")
      .replace(/&amp;/gi, '&')
      .replace(/\n{3,}/g, '\n\n')
      .trim()
  );
};

/**
 * Turn a legacy plain-text description into the HTML the editor expects.
 *
 * Escaping first matters: a legacy value containing `<` would otherwise be re-parsed as
 * markup by the editor.
 */
export const plainTextToHtml = (text?: string | null): string => {
  if (!text) {
    return '';
  }

  const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  return escaped
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${paragraph.replace(/\n/g, '<br>')}</p>`)
    .join('');
};

/**
 * True when the document renders as nothing.
 *
 * Tiptap emits `<p></p>` for an empty document — truthy, seven characters — so a naive
 * `!value` check would pass validation, store junk markup, and make "the user cleared the
 * field" indistinguishable from real content.
 */
export const isRichTextEmpty = (html?: string | null): boolean =>
  htmlToPlainText(html).trim().length === 0;

/**
 * Canonical submit-time normalizer: anything that renders as nothing becomes `null`.
 *
 * Returning `null` rather than `undefined` is what lets a cleared description actually reach
 * the database — TypeORM skips `undefined` but writes `null`.
 */
export const normalizeRichTextForSubmit = (html?: string | null): string | null =>
  isRichTextEmpty(html) ? null : (html as string);
